/* People's Peps: plain-English peptide calculator (v2).

   One tool for both questions a researcher arrives with:
     "I haven't added water yet. How much do I add?"            (mode "new")
     "I already added water. How much do I measure?"            (mode "mixed")
   and a blend or a stack of vials, one card per vial.

   The arithmetic is the same as pp-calc.js and pp-bacwater.js:
     strength (mg per mL)  = ingredient mg in the vial / water mL
     draw (mL)             = amount (mg) / strength
     scale mark            = draw mL x 100 (shown to the visitor in mL)
   What changed is the order: work out which question is being asked, then
   write the one sentence that answers it (brief item 7). The numbers behind
   it sit under "Show the math".

   Markup: sections/pp-calc2.liquid and pp-calc2-mini.liquid render
   snippets/pp-calc2-tool.liquid, which carries the product list and every
   word as JSON, plus a <template> for one vial card. Nothing customer-facing
   is written in this file except units (mL, mcg, mg).

   Customizer-safe: one re-entry guard, each tool initialises once and again
   on shopify:section:load. Arithmetic only; nothing leaves the page except
   the add-to-cart and the "Send me this stack" signup the visitor asks for. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* pure arithmetic (also exported for tests)                           */
  /* ------------------------------------------------------------------ */
  var EPS = 1e-9;
  var LINE_TOL = 0.001;            /* units: "on a printed line" within this */

  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : NaN;
    v = String(v == null ? '' : v).trim().replace(/,/g, '.').replace(/[^0-9.\-]/g, '');
    if (!v || v === '.' || v === '-') return NaN;
    var n = parseFloat(v);
    return isFinite(n) ? n : NaN;
  }
  /* fixed decimals, then drop trailing zeros (never touches integer zeros) */
  function trimNum(n, d) {
    var f = Math.pow(10, d);
    var s = (Math.round(n * f) / f).toFixed(d);
    if (s.indexOf('.') >= 0) s = s.replace(/0+$/, '').replace(/\.$/, '');
    if (s === '-0') s = '0';
    return s;
  }
  function group(s) {
    var p = String(s).split('.');
    p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return p.join('.');
  }
  function lineInfo(units, step) {
    var k = units / step;
    var r = Math.round(k);
    var on = Math.abs(k - r) * step < LINE_TOL;
    var below = on ? r * step : Math.floor(k + EPS) * step;
    var above = on ? r * step : below + step;
    return { on: on, below: below, above: above, dist: on ? 0 : Math.min(units - below, above - units) };
  }

  /* Item 4: pick the round water amount that puts the draw on a printed line.
     waters: candidate mL list; maxW: vial max; minU: smallest usable draw. */
  function pickWater(anchorMg, amtMg, syr, waters, maxW, minU) {
    var res = [];
    waters.forEach(function (w) {
      if (!(w > 0) || w > maxW + EPS) return;
      var conc = anchorMg / w;
      var units = amtMg / conc * 100;
      var li = lineInfo(units, syr.step);
      res.push({ w: w, units: units, fits: units <= syr.units + 1e-7, big: units >= minU - 1e-7, on: li.on, li: li });
    });
    if (!res.length) return { state: 'none', res: res };
    var ok = res.filter(function (r) { return r.fits && r.big; });
    if (!ok.length) {
      var allOver = res.every(function (r) { return !r.fits; });
      return { state: allOver ? 'over' : 'small', res: res };
    }
    var on = ok.filter(function (r) { return r.on; });
    if (on.length) {
      on.sort(function (a, b) { return b.units - a.units; });
      return { state: 'on', r: on[0], res: res };
    }
    ok.sort(function (a, b) {
      var d = a.li.dist - b.li.dist;
      if (Math.abs(d) > 1e-9) return d;
      return b.units - a.units;                   /* tie goes to the bigger draw */
    });
    return { state: 'near', r: ok[0], res: res };
  }

  /* A syringe that does put it on a line: biggest draw wins, then the finer scale. */
  function suggestSyringe(anchorMg, amtMg, syrs, curIdx, waters, maxW, minU) {
    var best = null;
    syrs.forEach(function (s, i) {
      if (i === curIdx) return;
      var p = pickWater(anchorMg, amtMg, s, waters, maxW, minU);
      if (p.state !== 'on') return;
      if (!best || p.r.units > best.r.units + 1e-9 ||
          (Math.abs(p.r.units - best.r.units) < 1e-9 && s.units < syrs[best.i].units)) {
        best = { i: i, r: p.r };
      }
    });
    return best;
  }

  var math = { num: num, trimNum: trimNum, group: group, lineInfo: lineInfo, pickWater: pickWater, suggestSyringe: suggestSyringe };
  if (typeof module !== 'undefined' && module.exports) { module.exports = math; return; }
  window.PPCalc2Math = math;

  if (window.__ppCalc2Wired) return;
  window.__ppCalc2Wired = true;

  /* ------------------------------------------------------------------ */
  /* formatting                                                          */
  /* ------------------------------------------------------------------ */
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  /* "[token]" replaced by already-safe HTML; "**x**" becomes <strong>x</strong> */
  function fill(tpl, map) {
    var out = esc(tpl || '').replace(/\[([a-z0-9_]+)\]/gi, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
    });
    return out.replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>');
  }
  function plain(html) {
    var d = document.createElement('div');
    d.innerHTML = html;
    return d.textContent.replace(/\s+/g, ' ').trim();
  }
  /* amounts we work out: mcg under 1,000; at 1,000 and up "2.5 mg (2,500 mcg)" */
  function fmtMass(mg) {
    var mcg = mg * 1000;
    if (mcg < 1000 - 1e-6) return group(trimNum(mcg, 2)) + ' mcg';
    return group(trimNum(mg, 3)) + ' mg (' + group(trimNum(mcg, 1)) + ' mcg)';
  }
  /* the amount they typed, in the unit they typed it */
  function fmtTyped(v, unit) {
    if (unit === 'mg') return group(trimNum(v, 4)) + ' mg';
    if (v < 1000 - 1e-6) return group(trimNum(v, 2)) + ' mcg';
    return fmtMass(v / 1000);
  }
  function fmtStrength(mgPerMl) {
    if (mgPerMl < 1 - 1e-9) return group(trimNum(mgPerMl * 1000, 1)) + ' mcg';
    return group(trimNum(mgPerMl, 2)) + ' mg';
  }
  function fmtUnits(u) { return trimNum(u, 2); }
  function fmtMl(w) { return trimNum(w, 2) + ' mL'; }
  function fmtDraw(w) { return trimNum(w, 3) + ' mL'; }

  function parseList(str) {
    return String(str || '').split(',').map(function (x) { return num(x); })
      .filter(function (x) { return x > 0; })
      .sort(function (a, b) { return a - b; });
  }
  function parseSyr(str) {
    var p = String(str || '').split('|').map(function (x) { return x.trim(); });
    if (p.length < 3) return null;
    var units = num(p[1]), step = num(p[2]);
    if (!p[0] || !(units > 0) || !(step > 0)) return null;
    return { name: p[0], units: units, step: step, ml: units / 100 };
  }
  /* "10 mg" / "500 mcg" */
  function parseMass(str) {
    var n = num(str);
    if (!isFinite(n)) return NaN;
    return /mcg|µg|ug/i.test(String(str)) ? n / 1000 : n;
  }
  function slug(s) {
    return String(s || '').toLowerCase().replace(/\+/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* ------------------------------------------------------------------ */
  /* one tool                                                            */
  /* ------------------------------------------------------------------ */
  function Tool(root) {
    this.root = root;
    this.mini = root.getAttribute('data-variant') === 'mini';
    this.cfgOk = this.readConfig();
    if (!this.cfgOk) return;
    this.cards = [];
    this.seq = 0;
    this.cardsEl = root.querySelector('[data-c2-cards]');
    this.tpl = root.querySelector('template[data-c2-tpl]');
    this.addBtn = root.querySelector('[data-c2-add]');
    this.atc = root.querySelector('[data-c2-atc]');
    this.atcMsg = root.querySelector('[data-c2-atc-msg]');
    this.linkBtn = root.querySelector('[data-c2-link]');
    this.fullLink = root.querySelector('[data-c2-full]');
    this.send = root.querySelector('[data-c2-send]');
    this.bind();
    if (!this.restore()) this.addCard({});
    this.refreshChrome();
  }

  Tool.prototype.readConfig = function () {
    var root = this.root;
    try {
      this.W = JSON.parse(root.querySelector('script[data-c2-words]').textContent);
      var cfg = JSON.parse(root.querySelector('script[data-c2-config]').textContent);
      var prods = JSON.parse(root.querySelector('script[data-c2-products]').textContent);
      this.cfg = cfg;
      this.syrs = (cfg.syringes || []).map(parseSyr).filter(Boolean);
      this.waters = parseList(cfg.waters);
      this.minU = num(cfg.min_units) > 0 ? num(cfg.min_units) : 5;
      this.maxVials = Math.max(1, Math.round(num(cfg.max_vials)) || 6);
      this.defSyr = Math.min(Math.max(0, (Math.round(num(cfg.default_syringe)) || this.syrs.length) - 1), this.syrs.length - 1);
      this.products = prods.map(function (p) { return normalise(p); }).filter(function (p) { return p && p.comps.length; });
      this.byHandle = {};
      this.byVariant = {};
      var self = this;
      this.products.forEach(function (p) {
        self.byHandle[p.h] = p;
        if (p.v) self.byVariant[String(p.v)] = p.h;
      });
    } catch (e) {
      if (window.console) console.warn('PP calculator: bad config', e);
      return false;
    }
    return !!(this.syrs.length && this.waters.length && this.products.length);
  };

  function normalise(p) {
    var comps = [];
    if (Array.isArray(p.c)) {
      p.c.forEach(function (c) {
        var mg = num(c && c.mg);
        if (c && c.name && mg > 0) comps.push({ name: String(c.name), mg: mg, tag: c.tag ? String(c.tag) : slug(c.name), mw: num(c.mw) > 0 ? num(c.mw) : NaN });
      });
    }
    if (!comps.length) {                        /* fallback: the vial's nominal content */
      var mg = parseMass(p.nn);
      if (mg > 0) comps.push({ name: p.n || p.t, mg: mg, tag: slug(p.n || p.t), mw: num(p.mw) > 0 ? num(p.mw) : NaN });
    }
    var start = num(p.s), max = num(p.m);
    return {
      h: p.h, t: p.t, n: p.n || p.t, blend: comps.length > 1, comps: comps,
      start: start > 0 ? start : NaN, max: max > 0 ? max : NaN,
      v: p.v, a: p.a !== false, u: p.u
    };
  }

  /* ---------- cards ---------- */
  Tool.prototype.addCard = function (st) {
    if (this.cards.length >= this.maxVials) return null;
    var frag = this.tpl.content.cloneNode(true);
    var el = frag.querySelector('[data-card]');
    var c = new Card(this, el, ++this.seq, st || {});
    this.cards.push(c);
    this.cardsEl.appendChild(el);
    c.init();
    this.refreshChrome();
    return c;
  };
  Tool.prototype.removeCard = function (card) {
    var i = this.cards.indexOf(card);
    if (i < 0 || this.cards.length < 2) return;
    this.cards.splice(i, 1);
    card.el.parentNode.removeChild(card.el);
    this.refreshChrome();
    var next = this.cards[Math.min(i, this.cards.length - 1)];
    if (next) { var b = next.el.querySelector('[data-c2-mode][aria-pressed="true"]') || next.el.querySelector('[data-c2-mode]'); if (b) b.focus(); }
  };

  Tool.prototype.refreshChrome = function () {
    var self = this, many = this.cards.length > 1;
    this.cards.forEach(function (c, i) {
      c.setNumber(i + 1, many);
    });
    if (this.addBtn) this.addBtn.hidden = this.mini || this.cards.length >= this.maxVials;
    this.updateCta();
    this.syncUrl();
  };

  /* ---------- add to cart / waitlist ---------- */
  Tool.prototype.updateCta = function () {
    if (!this.atc) return;
    var answered = [], warned = false;
    this.cards.forEach(function (c) {
      if (c.result && c.result.warn) warned = true;
      if (c.result && c.result.answered) answered.push(c);
    });
    var show = answered.length > 0 && !warned;
    var wrap = this.root.querySelector('[data-c2-atc-wrap]');
    if (wrap) wrap.hidden = !show;
    var W = this.W;
    var label;
    if (this.cfg.ordering_closed) label = W.waitlist;
    else label = answered.length > 1 ? W.atc_stack : W.atc_one;
    var lab = this.atc.querySelector('[data-c2-atc-label]');
    if (lab && !this.atc.disabled) lab.textContent = label;
    this.answered = answered;
  };

  Tool.prototype.addToCart = function () {
    var self = this, W = this.W;
    if (this.cfg.ordering_closed) {
      var bar = document.querySelector('.pp-ann__open');
      if (bar) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (bar.getAttribute('aria-expanded') !== 'true') bar.click();
        setTimeout(function () { var f = document.querySelector('.pp-ann__form input[type="email"]'); if (f) f.focus(); }, 350);
      } else if (this.cfg.waitlist_url) {
        location.href = this.cfg.waitlist_url;
      }
      return;
    }
    var qty = {}, order = [];
    (this.answered || []).forEach(function (c) {
      var p = c.product();
      if (!p || !p.v || !p.a) return;
      if (!qty[p.v]) { qty[p.v] = 0; order.push(p.v); }
      qty[p.v] += 1;
    });
    if (!order.length) { this.say(W.atc_err, false); return; }
    var items = order.map(function (id) { return { id: Number(id), quantity: qty[id] }; });
    var lab = this.atc.querySelector('[data-c2-atc-label]');
    this.atc.disabled = true;
    if (lab) lab.textContent = W.atc_busy || lab.textContent;
    this.say('', false);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    fetch(root + 'cart/add.js', { method: 'POST', headers: json, credentials: 'same-origin', body: JSON.stringify({ items: items }) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function () {
        return fetch(root + 'cart/update.js', { method: 'POST', headers: json, credentials: 'same-origin', body: JSON.stringify({ attributes: { source: self.cfg.source || 'calculator' } }) })
          .catch(function () { return null; });
      })
      .then(function () {
        var code = String(self.cfg.code || '').trim();
        location.href = code ? root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart') : root + 'cart';
      })
      .catch(function () {
        self.atc.disabled = false;
        self.updateCta();
        self.say(W.atc_err, false);
      });
  };
  Tool.prototype.say = function (msg, ok) {
    if (!this.atcMsg) return;
    this.atcMsg.textContent = msg || '';
    this.atcMsg.classList.toggle('is-ok', !!ok);
  };

  /* ---------- share link (item 10) and the old ?p=&mg= links ---------- */
  Tool.prototype.state = function () {
    return this.cards.map(function (c) { return c.serial(); }).filter(Boolean);
  };
  /* One key per vial (c1, c2, c3...). A repeated key (c=..&c=..) is not used:
     some redirects keep only the last value of a repeated key, which would
     drop all but one vial from a shared link. */
  var LINK_KEYS = 12;
  function stackQuery(list) {
    return list.map(function (s, i) { return 'c' + (i + 1) + '=' + encodeURIComponent(s); }).join('&');
  }
  Tool.prototype.link = function () {
    var base = this.mini && this.cfg.page_path ? location.origin + this.cfg.page_path : location.origin + location.pathname;
    var q = stackQuery(this.state());
    return base + (q ? '?' + q : '');
  };
  Tool.prototype.syncUrl = function () {
    if (this.restoring) return;
    /* the product page's "Open the full calculator" link carries this vial */
    if (this.fullLink && this.cfg.page_path) this.fullLink.href = this.link();
    if (this.mini) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (!this.cfg.sync_url) return;
    try {
      var u = new URL(location.href);
      u.searchParams.delete('c');
      for (var k = 1; k <= LINK_KEYS; k++) u.searchParams.delete('c' + k);
      ['p', 'mg', 'ml', 'mw', 'amt', 'u', 'mark'].forEach(function (k2) { u.searchParams.delete(k2); });
      var rest = u.searchParams.toString(), mine = stackQuery(this.state());
      u.search = [rest, mine].filter(Boolean).join('&');
      history.replaceState(history.state, '', u.toString());
    } catch (e) { /* old browser: the Copy link button still works */ }
  };
  Tool.prototype.restore = function () {
    if (window.Shopify && window.Shopify.designMode) return false;
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { return false; }
    var list = [], self = this, made = 0;
    for (var k = 1; k <= LINK_KEYS; k++) { if (q.get('c' + k)) list.push(q.get('c' + k)); }
    q.getAll('c').forEach(function (s) { if (s) list.push(s); });
    this.restoring = true;
    if (this.mini) list = [];
    list.slice(0, this.maxVials).forEach(function (s) {
      var st = parseSerial(s);
      if (st && self.byHandle[st.h]) { self.addCard(st); made++; }
    });
    if (!made && !this.mini && q.get('p') && this.byHandle[q.get('p')]) {
      /* links from the two old calculators: ?p=handle&mg=&ml=&amt=&u= (water
         added) or ?p=&mg=&amt=&u=&mark= (BAC water, water not added yet) */
      var u = q.get('u') === 'mg' ? 'mg' : 'mcg';
      var st2 = { h: q.get('p'), unit: u, amt: q.get('amt') || '' };
      if (q.get('mark') !== null && q.get('ml') === null) st2.mode = 'new';
      else { st2.mode = 'mixed'; st2.water = q.get('ml') || ''; }
      this.addCard(st2); made++;
    }
    this.restoring = false;
    return made > 0;
  };
  function parseSerial(s) {
    var p = String(s || '').split('!');
    if (!p[0]) return null;
    return {
      h: p[0], mode: p[1] === 'n' ? 'new' : (p[1] === 'm' ? 'mixed' : ''),
      anchor: Math.max(0, Math.round(num(p[2])) || 0),
      amt: p[3] || '', unit: p[4] === 'mg' ? 'mg' : 'mcg',
      syr: isFinite(num(p[5])) ? Math.round(num(p[5])) : null,
      water: p[6] || '', pick: p[7] || ''
    };
  }

  Tool.prototype.copyLink = function () {
    var self = this, W = this.W, url = this.link();
    var lab = this.linkBtn.querySelector('[data-c2-link-label]');
    var done = function () {
      if (lab) lab.textContent = W.link_copied;
      self.linkBtn.setAttribute('data-done', '1');
      setTimeout(function () { if (lab) lab.textContent = W.copy_link; self.linkBtn.removeAttribute('data-done'); }, 1800);
    };
    copyText(url, done);
  };
  function copyText(text, done) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () { legacyCopy(text); done(); });
    } else { legacyCopy(text); done(); }
  }
  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.top = '-1000px';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) { /* nothing to do */ }
    document.body.removeChild(ta);
  }

  /* ---------- Send me this stack: a newsletter signup ---------- */
  Tool.prototype.stackText = function () {
    var lines = [];
    this.cards.forEach(function (c) {
      if (!c.result || !c.result.text) return;
      /* name the vial: the "already added water" sentence doesn't */
      var p = c.product();
      lines.push((c.tool.cards.length > 1 ? c.label() + ', ' : '') + (p ? p.t + ': ' : '') + c.result.text);
    });
    return lines.join('\n');
  };
  Tool.prototype.tags = function () {
    var cfg = this.cfg, set = {}, out = [];
    var add = function (t) { t = String(t || '').trim(); if (t && !set[t]) { set[t] = 1; out.push(t); } };
    String(cfg.tag_stack || '').split(',').forEach(add);
    this.cards.forEach(function (c) {
      var p = c.product();
      if (!p || !c.mode) return;
      p.comps.forEach(function (x) { add((cfg.tag_prefix || '') + x.tag); });
    });
    return out;
  };
  Tool.prototype.submitSend = function (form) {
    var self = this, W = this.W;
    var email = form.querySelector('input[type="email"]');
    var btn = form.querySelector('button[type="submit"]');
    var msg = form.querySelector('[data-c2-send-msg]');
    var say = function (t, ok) { if (msg) { msg.textContent = t || ''; msg.classList.toggle('is-ok', !!ok); } };
    if (!email || !email.value || (email.checkValidity && !email.checkValidity())) { if (email && email.reportValidity) email.reportValidity(); return; }
    var tags = this.tags();
    var link = this.link();
    var when = new Date().toISOString();
    var text = this.stackText();
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var action = root + 'contact';
    if (btn) btn.disabled = true;
    say('', false);

    var p = new URLSearchParams();
    p.set('form_type', 'customer');
    p.set('utf8', '✓');
    p.set('contact[email]', email.value);
    p.set('contact[tags]', tags.join(', '));
    fetch(action, { method: 'POST', body: p, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (/\/challenge/.test(res.url)) return 'challenge';
        if (!res.ok) throw new Error('signup');
        /* Shopify answers a good signup by redirecting to ?customer_posted=true */
        if (!/customer_posted=true/.test(res.url)) throw new Error('signup');
        return 'sent';
      })
      .then(function (state) {
        if (state === 'challenge') {
          /* Shopify wants a spam check: post the signup the normal way */
          var f = document.createElement('form');
          f.method = 'post'; f.action = action; f.style.display = 'none';
          [['form_type', 'customer'], ['utf8', '✓'], ['contact[email]', email.value], ['contact[tags]', tags.join(', ')]].forEach(function (kv) {
            var i = document.createElement('input'); i.type = 'hidden'; i.name = kv[0]; i.value = kv[1]; f.appendChild(i);
          });
          document.body.appendChild(f); f.submit();
          return 'native';
        }
        /* the email tool: Omnisend reads this event once its app embed is on */
        try {
          if (window.omnisend && typeof window.omnisend.identifyContact === 'function') {
            window.omnisend.identifyContact({ email: email.value });
          }
          if (window.omnisend && typeof window.omnisend.push === 'function' && self.cfg.omnisend_event) {
            window.omnisend.push(['track', self.cfg.omnisend_event, { stack: text, stack_link: link, calculated_at: when, ingredients: tags.join(', ') }]);
          }
        } catch (e) { /* the signup already went through */ }
        try {
          self.root.dispatchEvent(new CustomEvent('pp:calc-signup', { bubbles: true, detail: { email: email.value, tags: tags, stack: text, link: link, time: when } }));
        } catch (e) { /* old browser */ }
        /* a copy of the stack for the team, through the store's contact form */
        if (self.cfg.send_inbox) {
          var c = new URLSearchParams();
          c.set('form_type', 'contact');
          c.set('utf8', '✓');
          c.set('contact[email]', email.value);
          c.set('contact[Request]', W.send_btn || 'Send me this stack');
          c.set('contact[body]', [text, '', link, when].join('\n'));
          return fetch(action, { method: 'POST', body: c, credentials: 'same-origin', headers: { Accept: 'text/html' } })
            .then(function () { return 'sent'; }, function () { return 'sent'; });
        }
        return 'sent';
      })
      .then(function (state) {
        if (state !== 'sent') return;
        var row = form.querySelector('[data-c2-send-row]');
        if (row) row.hidden = true;
        say(W.send_ok, true);
        if (msg) { msg.setAttribute('tabindex', '-1'); msg.focus(); }
      })
      .catch(function () { say(W.send_err, false); })
      .then(function () { if (btn) btn.disabled = false; });
  };

  /* ---------- events ---------- */
  Tool.prototype.bind = function () {
    var self = this;
    if (this.addBtn) this.addBtn.addEventListener('click', function () {
      var last = self.cards[self.cards.length - 1];
      var c = self.addCard({ mode: last && last.mode ? last.mode : '', syr: last ? last.syr : null });
      if (c) { var s = c.el.querySelector('select'); if (s && c.mode) s.focus(); else { var b = c.el.querySelector('[data-c2-mode]'); if (b) b.focus(); } }
    });
    if (this.atc) this.atc.addEventListener('click', function () { self.addToCart(); });
    if (this.linkBtn) this.linkBtn.addEventListener('click', function () { self.copyLink(); });
    if (this.send) {
      var f = this.send.querySelector('form');
      if (f) f.addEventListener('submit', function (e) { e.preventDefault(); self.submitSend(f); });
    }
  };

  /* ------------------------------------------------------------------ */
  /* one vial card                                                       */
  /* ------------------------------------------------------------------ */
  function Card(tool, el, seq, st) {
    this.tool = tool; this.el = el; this.seq = seq;
    var p = tool.byHandle[st.h] || (tool.cfg.fixed && tool.byHandle[tool.cfg.fixed]) || null;
    if (!p) {
      var def = tool.cfg.default_product && tool.byHandle[tool.cfg.default_product];
      p = def || tool.products[0];
    }
    this.h = p.h;
    var startMode = tool.cfg.start_mode === 'new' || tool.cfg.start_mode === 'mixed' ? tool.cfg.start_mode : '';
    this.mode = st.mode === 'new' || st.mode === 'mixed' ? st.mode : startMode;
    this.anchor = Math.min(st.anchor || 0, p.comps.length - 1);
    this.unit = st.unit === 'mg' ? 'mg' : 'mcg';
    this.amtText = st.amt || '';
    this.syr = st.syr != null && st.syr >= 0 && st.syr < tool.syrs.length ? st.syr : tool.defSyr;
    this.waterText = st.water || '';
    this.pick = st.pick && num(st.pick) > 0 ? num(st.pick) : 'auto';
    this.result = null;
  }
  Card.prototype.q = function (s) { return this.el.querySelector(s); };
  Card.prototype.product = function () { return this.tool.byHandle[this.h]; };
  Card.prototype.label = function () { return plain(fill(this.tool.W.vial_n, { n: String(this.num || 1) })); };

  Card.prototype.init = function () {
    var self = this, tool = this.tool, W = tool.W, uid = 'c2-' + (tool.root.id || 'x') + '-' + this.seq;
    var ids = ['product', 'amount', 'water'];
    ids.forEach(function (k) {
      var inp = self.q('[data-c2-' + k + ']'), lab = self.q('[data-c2-for="' + k + '"]');
      if (inp) inp.id = uid + '-' + k;
      if (lab && inp) lab.setAttribute('for', inp.id);
    });
    var ans = this.q('[data-c2-answer]');
    if (ans) ans.id = uid + '-ans';

    /* products */
    var sel = this.q('[data-c2-product]');
    if (sel) {
      tool.products.forEach(function (p) {
        var o = document.createElement('option');
        o.value = p.h; o.textContent = p.t;
        if (p.h === self.h) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () { self.setProduct(sel.value); });
    }
    var fixed = this.q('[data-c2-fixed]');
    if (fixed) fixed.textContent = this.product().t;

    /* modes */
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-mode]'), function (b) {
      b.addEventListener('click', function () { self.setMode(b.getAttribute('data-c2-mode')); });
    });
    var rm = this.q('[data-c2-remove]');
    if (rm) rm.addEventListener('click', function () { tool.removeCard(self); });

    /* syringes */
    var sy = this.q('[data-c2-syringes]');
    if (sy) {
      tool.syrs.forEach(function (s, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'pp-c2pill'; b.setAttribute('data-i', String(i));
        b.textContent = s.name;
        b.addEventListener('click', function () { self.syr = i; self.update(); });
        sy.appendChild(b);
      });
    }

    /* amount + unit */
    var amt = this.q('[data-c2-amount]');
    if (amt) {
      amt.value = this.amtText;
      amt.addEventListener('input', function () { self.amtText = amt.value; self.update(); });
    }
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-unit]'), function (b) {
      b.addEventListener('click', function () { self.setUnit(b.getAttribute('data-c2-unit')); });
    });

    /* water added (mixed) */
    var wa = this.q('[data-c2-water]');
    if (wa) {
      wa.value = this.waterText;
      wa.addEventListener('input', function () { self.waterText = wa.value; self.update(); });
    }
    var wp = this.q('[data-c2-water-pills]');
    if (wp) {
      tool.waters.forEach(function (w) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'pp-c2pill'; b.setAttribute('data-w', String(w));
        b.textContent = fmtMl(w);
        b.addEventListener('click', function () { self.waterText = trimNum(w, 2); if (wa) wa.value = self.waterText; self.update(); });
        wp.appendChild(b);
      });
    }

    /* water to add (new): Best fit, or a fixed amount */
    var pk = this.q('[data-c2-picks]');
    if (pk) {
      var auto = document.createElement('button');
      auto.type = 'button'; auto.className = 'pp-c2pill pp-c2pill--auto'; auto.setAttribute('data-w', 'auto');
      auto.textContent = W.best_fit;
      auto.addEventListener('click', function () { self.pick = 'auto'; self.update(); });
      pk.appendChild(auto);
      tool.waters.forEach(function (w) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'pp-c2pill'; b.setAttribute('data-w', String(w));
        b.textContent = fmtMl(w);
        b.addEventListener('click', function () { self.pick = w; self.update(); });
        pk.appendChild(b);
      });
    }

    /* the suggestion's switch button and the math copy button */
    var sw = this.q('[data-c2-switch]');
    if (sw) sw.addEventListener('click', function () {
      var i = num(sw.getAttribute('data-i'));
      if (isFinite(i)) { self.syr = i; self.pick = 'auto'; self.update(); }
    });
    var cp = this.q('[data-c2-copy-table]');
    if (cp) cp.addEventListener('click', function () {
      var t = self.mathText || '';
      copyText(t, function () {
        cp.textContent = W.m_copied;
        setTimeout(function () { cp.textContent = W.m_copy; }, 1600);
      });
    });

    if (this.mode === 'mixed' && !this.waterText) this.waterText = this.defaultWater();
    if (wa && this.mode === 'mixed') wa.value = this.waterText;
    this.update();
  };

  Card.prototype.setNumber = function (n, many) {
    this.num = n;
    var t = this.q('[data-c2-n]');
    if (t) { t.textContent = plain(fill(this.tool.W.vial_n, { n: String(n) })); t.hidden = !many; }
    var rm = this.q('[data-c2-remove]');
    if (rm) rm.hidden = !many;
    var top = this.q('[data-c2-top]');
    if (top) top.hidden = !many;
  };

  Card.prototype.defaultWater = function () {
    var p = this.product();
    var w = p && p.start > 0 ? p.start : (this.tool.waters.length ? this.tool.waters[Math.floor(this.tool.waters.length / 2)] : 2);
    if (p && p.max > 0 && w > p.max) w = p.max;
    return trimNum(w, 2);
  };

  Card.prototype.setMode = function (m) {
    if (m !== 'new' && m !== 'mixed') return;
    this.mode = m;
    if (m === 'mixed' && !num(this.waterText)) {
      this.waterText = this.defaultWater();
      var wa = this.q('[data-c2-water]');
      if (wa) wa.value = this.waterText;
    }
    this.update();
    var focusEl = this.q('[data-c2-amount]');
    if (focusEl && !this.amtText && window.matchMedia && window.matchMedia('(pointer:fine)').matches) focusEl.focus();
  };

  Card.prototype.setProduct = function (h) {
    var before = this.product(), after = this.tool.byHandle[h];
    if (!after) return;
    var anchorName = before ? before.comps[this.anchor] && before.comps[this.anchor].name : '';
    this.h = h;
    var idx = 0;
    after.comps.forEach(function (c, i) { if (c.name === anchorName) idx = i; });
    this.anchor = idx;
    /* a different ingredient means a different amount: start that box empty */
    if (!anchorName || after.comps[idx].name !== anchorName) {
      this.amtText = '';
      var amt = this.q('[data-c2-amount]');
      if (amt) amt.value = '';
    }
    this.pick = 'auto';
    if (this.mode === 'mixed') {
      this.waterText = this.defaultWater();
      var wa = this.q('[data-c2-water]');
      if (wa) wa.value = this.waterText;
    }
    var fixed = this.q('[data-c2-fixed]');
    if (fixed) fixed.textContent = after.t;
    this.update();
  };

  Card.prototype.setUnit = function (u) {
    if (u === this.unit) return;
    var a = num(this.amtText);
    if (isFinite(a)) {                             /* keep the physical amount */
      this.amtText = u === 'mg' ? trimNum(a / 1000, 4) : trimNum(a * 1000, 1);
      var amt = this.q('[data-c2-amount]');
      if (amt) amt.value = this.amtText;
    }
    this.unit = u;
    this.update();
  };

  Card.prototype.serial = function () {
    if (!this.h) return '';
    var m = this.mode === 'new' ? 'n' : (this.mode === 'mixed' ? 'm' : '');
    var parts = [this.h, m, String(this.anchor), String(this.amtText || '').replace(/[^0-9.]/g, ''), this.unit, String(this.syr),
                 this.mode === 'mixed' ? String(this.waterText || '').replace(/[^0-9.]/g, '') : '',
                 this.mode === 'new' && this.pick !== 'auto' ? trimNum(this.pick, 2) : ''];
    while (parts.length > 1 && parts[parts.length - 1] === '') parts.pop();
    return parts.join('!');
  };

  /* ---------- the answer ---------- */
  Card.prototype.compute = function () {
    var tool = this.tool, W = tool.W, p = this.product();
    var syr = tool.syrs[this.syr], A = p.comps[this.anchor];
    var r = { answered: false, warn: false, sentence: '', text: '', each: [], tip: null, conc: NaN, units: NaN, water: NaN };
    if (!this.mode) return r;

    var syrName = fill(W.syringe_name, { name: esc(syr.name) });
    var amtV = num(this.amtText);
    var amtMg = isFinite(amtV) && amtV > 0 ? (this.unit === 'mg' ? amtV : amtV / 1000) : NaN;
    var amountHtml = isFinite(amtMg) ? esc(fmtTyped(amtV, this.unit)) : '';
    if (p.blend && amountHtml) amountHtml = fill(W.amount_of, { amount: amountHtml, name: esc(A.name) });
    var vialMg = esc(group(trimNum(A.mg, 3)) + ' mg');
    if (p.blend) vialMg = fill(W.amount_of, { amount: vialMg, name: esc(A.name) });
    var maxW = p.max > 0 ? p.max : Infinity;
    var self = this;

    var drawsHtml = function (n) {
      return n === 1 ? fill(W.draw_one, { n: '1' }) : fill(W.draw_many, { n: group(String(n)) });
    };
    var alsoList = function (drawMl) {
      if (!p.blend) return '';
      var parts = [];
      p.comps.forEach(function (c, i) {
        if (i === self.anchor) return;
        parts.push(fill(W.amount_of, { amount: esc(fmtMass(c.mg / r.water * drawMl)), name: esc(c.name) }));
      });
      return joinList(parts, W.and);
    };
    var eachDraw = function (drawMl) {
      return p.comps.map(function (c) { return { name: c.name, text: fmtMass(c.mg / r.water * drawMl) }; });
    };
    var where = function (li) {
      if (r.units - li.below <= li.above - r.units) return fill(W.where_past, { n: esc(fmtDraw(li.below / 100)) });
      return fill(W.where_short, { n: esc(fmtDraw(li.above / 100)) });
    };

    if (this.mode === 'new') {
      if (!isFinite(amtMg)) { r.prompt = W.p_amount; return r; }
      if (amtMg > A.mg + 1e-9) { r.warn = true; r.warnHtml = fill(W.w_vial, { mg: vialMg }); return r; }
      var bigger = tool.syrs.some(function (s) { return s.units > syr.units; });
      var smaller = tool.syrs.some(function (s) { return s.units < syr.units; });
      var waters = tool.waters.filter(function (w) { return w <= maxW + EPS; });
      var chosen;
      if (this.pick !== 'auto') {
        var w = Math.min(this.pick, maxW);
        var units0 = amtMg / (A.mg / w) * 100;
        chosen = { w: w, units: units0, li: lineInfo(units0, syr.step), fits: units0 <= syr.units + 1e-7 };
        if (!chosen.fits) {
          r.warn = true;
          r.warnHtml = fill(bigger ? W.w_over_new : W.w_over_new_max, { syringe: syrName, water: esc(fmtMl(w)) });
          return r;
        }
      } else {
        var pk = pickWater(A.mg, amtMg, syr, waters, maxW, tool.minU);
        if (pk.state === 'none') { r.prompt = W.p_amount; return r; }
        if (pk.state === 'over') {
          r.warn = true;
          r.warnHtml = fill(bigger ? W.w_over_new : W.w_over_new_max, { syringe: syrName, water: esc(fmtMl(waters[0])) });
          return r;
        }
        if (pk.state === 'small') {
          r.warn = true;
          r.warnHtml = fill(W.w_small, { water: esc(fmtMl(waters[waters.length - 1])), min: esc(trimNum(tool.minU, 2)), syringe: syrName }) +
                       (smaller ? ' ' + fill(W.w_smaller, {}) : '');
          return r;
        }
        chosen = pk.r;
        if (pk.state === 'near') {
          var sug = suggestSyringe(A.mg, amtMg, tool.syrs, this.syr, waters, maxW, tool.minU);
          if (sug) {
            var s2 = tool.syrs[sug.i];
            r.tip = {
              html: fill(W.tip, { syringe: fill(W.syringe_name, { name: esc(s2.name) }), water: esc(fmtMl(sug.r.w)), units: esc(fmtUnits(sug.r.units)), ml: esc(fmtDraw(sug.r.units / 100)) }),
              btn: plain(fill(W.switch_to, { syringe: fill(W.syringe_name, { name: esc(s2.name) }) })),
              i: sug.i
            };
          }
        }
      }
      r.water = chosen.w;
      r.conc = A.mg / chosen.w;
      r.units = chosen.units;
      r.drawMl = chosen.units / 100;
      var draws = Math.floor(A.mg / amtMg + 1e-9);
      var also = alsoList(r.drawMl);
      var map = {
        water: esc(fmtMl(chosen.w)), vial: esc(p.n), units: esc(fmtUnits(chosen.units)), ml: esc(fmtDraw(chosen.units / 100)), syringe: syrName,
        amount: amountHtml, draws: drawsHtml(draws),
        also: also ? fill(W.s_also, { list: also }) + ' ' : '',
        where: chosen.li.on ? '' : where(chosen.li)
      };
      r.sentence = fill(chosen.li.on ? W.s_new : W.s_new_off, map);
      r.each = p.blend ? eachDraw(r.drawMl) : [];
      r.answered = true;
    } else {
      var wv = num(this.waterText);
      if (!(wv > 0)) { r.prompt = W.p_water; return r; }
      r.water = wv;
      r.conc = A.mg / wv;
      if (!isFinite(amtMg)) {
        var per10 = r.conc * 0.1;
        var strength = esc(fmtStrength(r.conc)), per = esc(fmtMass(per10));
        if (p.blend) {
          strength = fill(W.amount_of, { amount: strength, name: esc(A.name) });
          per = fill(W.amount_of, { amount: per, name: esc(A.name) });
        }
        var also10 = alsoList(0.1);
        r.sentence = fill(W.s_strength, { strength: strength, per10: per }) + (also10 ? ' ' + fill(W.s_also10, { list: also10 }) : '');
        r.each = p.blend ? eachDraw(0.1) : [];
        r.answered = true;
        r.noAmount = true;
      } else {
        if (amtMg > A.mg + 1e-9) { r.warn = true; r.warnHtml = fill(W.w_vial, { mg: vialMg }); return r; }
        r.units = amtMg / r.conc * 100;
        r.drawMl = r.units / 100;
        if (r.units > syr.units + 1e-7) {
          var big2 = tool.syrs.some(function (s) { return s.units > syr.units && r.units <= s.units + 1e-7; });
          r.warn = true;
          r.warnHtml = fill(W.w_over_mixed, { syringe: syrName }) + (big2 ? ' ' + fill(W.w_bigger, {}) : '');
          return r;
        }
        var li = lineInfo(r.units, syr.step);
        var draws2 = Math.floor(A.mg / amtMg + 1e-9);
        var also2 = alsoList(r.drawMl);
        r.sentence = fill(li.on ? W.s_mixed : W.s_mixed_off, {
          units: esc(fmtUnits(r.units)), ml: esc(fmtDraw(r.units / 100)), syringe: syrName, amount: amountHtml, draws: drawsHtml(draws2),
          also: also2 ? fill(W.s_also, { list: also2 }) + ' ' : '',
          where: li.on ? '' : where(li)
        });
        r.each = p.blend ? eachDraw(r.drawMl) : [];
        r.answered = true;
      }
    }
    r.text = plain(r.sentence);
    return r;
  };

  function joinList(parts, andWord) {
    if (parts.length < 2) return parts.join('');
    return parts.slice(0, -1).join(', ') + ' ' + esc(andWord || 'and') + ' ' + parts[parts.length - 1];
  }

  Card.prototype.update = function () {
    var tool = this.tool, W = tool.W, p = this.product(), self = this;
    var mode = this.mode;

    /* buttons and fields */
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-mode]'), function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-c2-mode') === mode ? 'true' : 'false');
    });
    this.el.classList.toggle('is-new', mode === 'new');
    this.el.classList.toggle('is-mixed', mode === 'mixed');
    var body = this.q('[data-c2-body]');
    if (body) body.hidden = !mode;
    var fA = this.q('[data-c2-f-anchor]');
    if (fA) {
      fA.hidden = !p.blend;
      var box = this.q('[data-c2-anchors]');
      if (box && box.getAttribute('data-h') !== p.h) {
        box.innerHTML = '';
        box.setAttribute('data-h', p.h);
        p.comps.forEach(function (c, i) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'pp-c2pill'; b.setAttribute('data-i', String(i));
          b.textContent = c.name;
          b.addEventListener('click', function () {
            if (self.anchor !== i) { self.anchor = i; self.amtText = ''; var a = self.q('[data-c2-amount]'); if (a) a.value = ''; }
            self.update();
          });
          box.appendChild(b);
        });
      }
      Array.prototype.forEach.call(box ? box.children : [], function (b) {
        b.setAttribute('aria-pressed', num(b.getAttribute('data-i')) === self.anchor ? 'true' : 'false');
      });
    }
    var comp = this.q('[data-c2-comp]');
    if (comp) {
      if (p.blend) {
        comp.innerHTML = fill(W.holds, { list: joinList(p.comps.map(function (c) { return esc(c.name + ' ' + group(trimNum(c.mg, 3)) + ' mg'); }), W.and) });
        comp.hidden = false;
      } else comp.hidden = true;
    }
    var fW = this.q('[data-c2-f-water]');
    if (fW) fW.hidden = mode !== 'mixed';
    var fP = this.q('[data-c2-f-pick]');
    if (fP) fP.hidden = mode !== 'new';
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-syringes] [data-i]'), function (b) {
      b.setAttribute('aria-pressed', num(b.getAttribute('data-i')) === self.syr ? 'true' : 'false');
    });
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-unit]'), function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-c2-unit') === self.unit ? 'true' : 'false');
    });
    var maxW = p.max > 0 ? p.max : Infinity;
    var wv = num(this.waterText);
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-water-pills] [data-w]'), function (b) {
      var w = num(b.getAttribute('data-w'));
      b.hidden = w > maxW + EPS;
      b.setAttribute('aria-pressed', Math.abs(w - wv) < 1e-9 ? 'true' : 'false');
    });
    Array.prototype.forEach.call(this.el.querySelectorAll('[data-c2-picks] [data-w]'), function (b) {
      var v = b.getAttribute('data-w');
      if (v === 'auto') { b.setAttribute('aria-pressed', self.pick === 'auto' ? 'true' : 'false'); return; }
      var w = num(v);
      b.hidden = w > maxW + EPS;
      b.setAttribute('aria-pressed', self.pick !== 'auto' && Math.abs(w - self.pick) < 1e-9 ? 'true' : 'false');
    });

    /* the answer */
    var r = this.result = this.compute();
    var sEl = this.q('[data-c2-sentence]');
    if (sEl) {
      if (r.sentence) { sEl.innerHTML = r.sentence; sEl.classList.remove('is-prompt'); }
      else if (r.prompt) { sEl.textContent = r.prompt; sEl.classList.add('is-prompt'); }
      else { sEl.textContent = ''; }
      sEl.hidden = !(r.sentence || r.prompt);
    }
    var wEl = this.q('[data-c2-warn]');
    if (wEl) { wEl.innerHTML = r.warn ? r.warnHtml : ''; wEl.hidden = !r.warn; }
    var tEl = this.q('[data-c2-tip]');
    if (tEl) {
      tEl.hidden = !r.tip;
      if (r.tip) {
        this.q('[data-c2-tip-text]').innerHTML = r.tip.html;
        var sw = this.q('[data-c2-switch]');
        sw.textContent = r.tip.btn;
        sw.setAttribute('data-i', String(r.tip.i));
      }
    }
    var eEl = this.q('[data-c2-each]');
    if (eEl) {
      var showEach = r.each && r.each.length > 1 && r.answered;
      eEl.hidden = !showEach;
      if (showEach) {
        var head = this.q('[data-c2-each-h]');
        if (head) head.textContent = r.noAmount ? W.each_10 : W.each_draw;
        var ul = this.q('[data-c2-each-list]');
        ul.innerHTML = r.each.map(function (x) { return '<li><span>' + esc(x.name) + '</span> <strong>' + esc(x.text) + '</strong></li>'; }).join('');
      }
    }
    this.drawSyringe(r);
    this.drawMath(r);
    tool.updateCta();
    tool.syncUrl();
  };

  /* ---------- item 5: the syringe picture ---------- */
  Card.prototype.drawSyringe = function (r) {
    var box = this.q('[data-c2-svg]');
    if (!box) return;
    var syr = this.tool.syrs[this.syr];
    var show = !!this.mode;
    box.hidden = !show;
    if (!show) { box.innerHTML = ''; return; }
    var cap = syr.units;
    var fillU = r.answered && !r.noAmount && isFinite(r.units) ? Math.min(r.units, cap) : (r.noAmount ? Math.min(10, cap) : 0);
    var VW = 640, x0 = 92, x1 = 572, yTop = 34, yBot = 78, mid = (yTop + yBot) / 2;
    var span = x1 - x0;
    var X = function (u) { return x0 + span * (u / cap); };
    var labelEvery = cap <= 50 ? 5 : 10;
    var majorEvery = cap <= 30 ? 1 : (cap <= 50 ? 5 : 10);
    var ticks = '', labels = '';
    var n = Math.round(cap / syr.step);
    for (var i = 0; i <= n; i++) {
      var u = i * syr.step;
      var isLabel = Math.abs(u / labelEvery - Math.round(u / labelEvery)) < 1e-6;
      var isMajor = isLabel || Math.abs(u / majorEvery - Math.round(u / majorEvery)) < 1e-6;
      var len = isLabel ? 20 : (isMajor ? 14 : 9);
      var x = X(u).toFixed(2);
      ticks += '<line x1="' + x + '" y1="' + yTop + '" x2="' + x + '" y2="' + (yTop + len) + '"/>';
      if (isLabel && u > 0) labels += '<text x="' + x + '" y="' + (yBot + 18) + '">' + trimNum(u / 100, 3) + '</text>';
    }
    var fx = X(fillU);
    var target = r.answered && !r.noAmount && isFinite(r.units) && r.units <= cap + 1e-7;
    var mark = '';
    if (target) {
      var tx = X(r.units).toFixed(2);
      mark = '<line class="pp-c2syr__target" x1="' + tx + '" y1="' + (yTop - 12) + '" x2="' + tx + '" y2="' + (yBot + 4) + '"/>' +
             '<text class="pp-c2syr__tlabel" x="' + tx + '" y="' + (yTop - 16) + '">' + esc(fmtDraw(r.units / 100)) + '</text>';
    }
    var title = r.text || this.tool.W.syringe_alt;
    box.innerHTML =
      '<svg class="pp-c2syr" viewBox="0 0 ' + VW + ' 120" role="img" aria-label="' + esc(title) + '" focusable="false">' +
        '<line class="pp-c2syr__needle" x1="6" y1="' + mid + '" x2="58" y2="' + mid + '"/>' +
        '<rect class="pp-c2syr__hub" x="58" y="' + (mid - 7) + '" width="20" height="14" rx="3"/>' +
        '<rect class="pp-c2syr__barrel" x="78" y="' + yTop + '" width="' + (x1 - 78 + 10) + '" height="' + (yBot - yTop) + '" rx="10"/>' +
        (fillU > 0 ? '<rect class="pp-c2syr__liquid" x="' + x0 + '" y="' + (yTop + 3) + '" width="' + Math.max(0, fx - x0).toFixed(2) + '" height="' + (yBot - yTop - 6) + '" rx="4"/>' : '') +
        '<g class="pp-c2syr__ticks">' + ticks + '</g>' +
        '<g class="pp-c2syr__nums">' + labels + '</g>' +
        '<rect class="pp-c2syr__stopper" x="' + (fx - 3).toFixed(2) + '" y="' + (yTop + 2) + '" width="9" height="' + (yBot - yTop - 4) + '" rx="2"/>' +
        '<line class="pp-c2syr__rod" x1="' + (fx + 6).toFixed(2) + '" y1="' + mid + '" x2="' + (VW - 22) + '" y2="' + mid + '"/>' +
        '<rect class="pp-c2syr__thumb" x="' + (VW - 22) + '" y="' + (mid - 22) + '" width="10" height="44" rx="3"/>' +
        mark +
        '<text class="pp-c2syr__name" x="' + (x1 + 4) + '" y="' + (yTop - 16) + '">' + esc(syr.name) + '</text>' +
      '</svg>';
  };

  /* ---------- "Show the math" ---------- */
  Card.prototype.drawMath = function (r) {
    var det = this.q('[data-c2-math]');
    if (!det) return;
    var p = this.product(), W = this.tool.W, syr = this.tool.syrs[this.syr];
    var ok = r.answered && isFinite(r.water) && r.water > 0;
    det.hidden = !ok;
    if (!ok) { this.mathText = ''; return; }
    var rows = '', lines = [];
    p.comps.forEach(function (c) {
      var conc = c.mg / r.water;
      var mm = c.mw > 0 ? conc / c.mw * 1000 : NaN;
      rows += '<tr><th scope="row">' + esc(c.name) + '</th><td>' + esc(trimNum(conc, 3)) + '</td><td>' + esc(group(trimNum(conc * 10, 2))) + '</td><td>' + (isFinite(mm) ? esc(trimNum(mm, 3)) : '&ndash;') + '</td></tr>';
      lines.push(c.name + '\t' + trimNum(conc, 3) + ' mg/mL\t' + trimNum(conc * 10, 2) + ' mcg per 0.01 mL' + (isFinite(mm) ? '\t' + trimNum(mm, 3) + ' mM' : ''));
    });
    var head = '<tr><th scope="col"></th><th scope="col">' + esc(W.m_mgml) + '</th><th scope="col">' + esc(W.m_mcgunit) + '</th><th scope="col">' + esc(W.m_mm) + '</th></tr>';
    var every = syr.units <= 50 ? 5 : 10;
    var tHead = '<tr><th scope="col">' + esc(W.m_vol) + '</th>' +
      p.comps.map(function (c) { return '<th scope="col">' + esc(c.name) + '</th>'; }).join('') + '</tr>';
    var tRows = '';
    var tLines = [W.m_vol + '\t' + p.comps.map(function (c) { return c.name; }).join('\t')];
    for (var u = every; u <= syr.units + 1e-9; u += every) {
      var ml = u / 100;
      tRows += '<tr><td>' + trimNum(ml, 2) + ' mL</td>' +
        p.comps.map(function (c) { return '<td>' + esc(fmtMass(c.mg / r.water * ml)) + '</td>'; }).join('') + '</tr>';
      tLines.push(trimNum(ml, 2) + ' mL\t' + p.comps.map(function (c) { return fmtMass(c.mg / r.water * ml); }).join('\t'));
    }
    var body = this.q('[data-c2-math-body]');
    body.innerHTML =
      '<p class="pp-c2math__water">' + fill(W.m_water, { water: esc(fmtMl(r.water)) }) + '</p>' +
      '<table class="pp-c2math__t">' + '<thead>' + head + '</thead><tbody>' + rows + '</tbody></table>' +
      '<table class="pp-c2math__t pp-c2math__t--lines"><thead>' + tHead + '</thead><tbody>' + tRows + '</tbody></table>';
    this.mathText = [p.t + ', ' + fmtMl(r.water), ''].concat(lines, [''], tLines).join('\n');
  };

  /* ------------------------------------------------------------------ */
  /* start                                                               */
  /* ------------------------------------------------------------------ */
  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-pp-c2]'), function (root) {
      if (root.__ppC2) return;
      root.__ppC2 = new Tool(root);
      root.setAttribute('data-ready', root.__ppC2.cfgOk ? '1' : '0');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /* A product sold in more than one strength: the product page's strength
     picker (assets/pp-pdp.js) announces the chosen variant. A mini calculator
     that lists that variant switches its vial to it; any other tool is left
     alone. */
  document.addEventListener('pp:variant', function (e) {
    var d = e.detail || {};
    if (!d.id) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-pp-c2]'), function (root) {
      var t = root.__ppC2;
      if (!t || !t.cfgOk || !t.mini || !t.byVariant) return;
      var key = t.byVariant[String(d.id)];
      if (!key) return;
      t.cards.forEach(function (c) { if (c.h !== key) c.setProduct(key); });
      t.syncUrl();
    });
  });
})();
